import { z } from "zod";

/**
 * BrandProfile v1 — the agency brand contract.
 *
 * The document lives in Supabase (`agency_brand_profiles.brand`), is edited
 * here and in the agency portal, and is stamped into the ML initiate envelope
 * by the WhatsApp bot (which validates with a dependency-free Python twin,
 * `bot/services/brand_profile.py`). Semantics are pinned by the shared vector
 * table `brand-profile.vectors.json` (byte-identical copies in all three
 * repos) — change the vectors first if the contract ever changes.
 *
 * Every section is optional: the profile is a sparse overlay and every
 * consumer treats a missing field as "behave as today". An empty object `{}`
 * (the column default) means "no profile yet" and parses to `null`.
 */

export const TONE_VOCABULARY = [
  "warm",
  "aspirational",
  "elegant",
  "modern",
  "minimal",
  "bold",
  "friendly",
  "professional",
  "luxurious",
  "serene",
] as const;

export const LOGO_KINDS = ["wordmark", "monogram", "favicon"] as const;

/**
 * STU-009 curated sets — the ONLY values fonts.selected / prefs.narrator /
 * prefs.music_palette accept. Pinned to the vectors' curated_* tables by test.
 * fonts.selected keys the composer's static @remotion/google-fonts imports
 * (EchoBrandOutro CURATED_FONTS); narrators are voice-library reference_names
 * (the two legacy names + the NAR archetype keys below);
 * palettes are the orchestrator's resources/music_palettes.yaml keys. The sets
 * grow by PR across all three repos at once — arbitrary values are never
 * rendered (deterministic renders, clean licensing).
 */
export const CURATED_FONTS = [
  "Cormorant Garamond",
  "Playfair Display",
  "Fraunces",
  "Libre Caslon Text",
  "Marcellus",
  "Cinzel",
  "DM Serif Display",
  "Montserrat",
  "Inter",
  "Manrope",
  "Archivo",
  "Prompt",
] as const;

/**
 * Real-estate narrator archetypes (NAR): six archetypes, each in a female and a
 * male form. A narrator key is `<archetype>_<gender>` and is, by contract, the
 * `reference_names` entry of its voice in the signed voice catalog. An absent
 * prefs.narrator means "Felix chooses". The agency portal is the editor; the
 * CRM only has to keep parsing (and carrying forward) whatever it saved.
 */
export const ARCHETYPE_NARRATORS = [
  "sophisticate_female",
  "sophisticate_male",
  "charmer_female",
  "charmer_male",
  "modernist_female",
  "modernist_male",
  "host_female",
  "host_male",
  "insider_female",
  "insider_male",
  "dreamer_female",
  "dreamer_male",
] as const;

/** Still valid so a saved profile never stops parsing; no editor offers them. */
export const LEGACY_NARRATORS = ["olivia_colman", "ian_mckellen"] as const;

export const CURATED_NARRATORS = [
  ...LEGACY_NARRATORS,
  ...ARCHETYPE_NARRATORS,
] as const;

export const CURATED_MUSIC_PALETTES = [
  "signature_luxury",
  "seafront_mediterranean",
  "historic_classic",
  "modern_minimal",
  "urban_penthouse",
  "countryside_estate",
] as const;

/**
 * PT-050 "Powered by Echoflicks" credit on the tour outro. "off" asks for the
 * credit-free outro; absent means "on" (behave as today). A preference, not an
 * entitlement: the bot honours "off" only for entitled agencies. Twin of the
 * portal schema and the bot's _check_prefs.
 */
export const POWERED_BY_VALUES = ["on", "off"] as const;
export type PoweredBy = (typeof POWERED_BY_VALUES)[number];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_DISPLAY_RE = /^[0-9+()\-. ]+$/;
const HTTP_URL_RE = /^https?:\/\//;

/** Non-blank, length-capped free text (mirrors the Python `_check_text`). */
const text = (maxLen: number) =>
  z
    .string()
    .max(maxLen)
    .refine((value) => value.trim().length > 0, "blank");

const hexColor = z.string().regex(HEX_RE, "expected #RRGGBB");

const identitySchema = z.strictObject({
  display_name: text(80).optional(),
  tagline: text(140).nullish(),
});

const paletteSchema = z.strictObject({
  primary: hexColor.optional(),
  accent: hexColor.optional(),
});

const logoSchema = z.strictObject({
  storage_path: text(300),
  kind: z.enum(LOGO_KINDS),
  source_url: z
    .string()
    .max(500)
    .regex(HTTP_URL_RE, "expected http(s) URL")
    .nullish(),
});

const contactSchema = z.strictObject({
  agent_name: text(80).optional(),
  phone_display: z
    .string()
    .max(32)
    .regex(PHONE_DISPLAY_RE, "digits, spaces and + ( ) . - only")
    .refine((value) => /[0-9]/.test(value), "needs at least one digit")
    .optional(),
  email: z.string().max(120).regex(EMAIL_RE, "invalid email").nullish(),
  website_display: text(100).optional(),
});

const toneSchema = z.strictObject({
  adjectives: z
    .array(z.enum(TONE_VOCABULARY))
    .max(5)
    .refine(
      (values) => new Set(values).size === values.length,
      "duplicate adjectives",
    )
    .optional(),
  // Captured, never consumed (prompt-injection guard) — length-capped only.
  notes: z.string().max(500).nullish(),
});

// Uploaded title faces (RE-05): the formats the composer's Remotion font loader
// accepts, at most six per agency, 5 MB each. `title_font_id` names the face the
// opening title is set in — an upload by id or a curated family — while
// `selected` stays the outro's curated face. Twin of the portal schema.
export const FONT_FORMATS = ["woff2", "woff", "truetype", "opentype"] as const;
export const FONT_STYLES = ["normal", "italic"] as const;
export const MAX_CUSTOM_FONTS = 6;
export const MAX_FONT_BYTES = 5 * 1024 * 1024;
const FONT_ID_RE = /^[a-z0-9][a-z0-9-]{1,39}$/;
const FONT_WEIGHT_RE = /^[1-9]00$/;

const customFontSchema = z.strictObject({
  id: z.string().regex(FONT_ID_RE, "font id"),
  family: text(80),
  storage_path: text(300),
  format: z.enum(FONT_FORMATS),
  weight: z.string().regex(FONT_WEIGHT_RE, "weight 100..900").optional(),
  style: z.enum(FONT_STYLES).optional(),
  original_name: text(120).optional(),
  size_bytes: z.number().int().min(1).max(MAX_FONT_BYTES).optional(),
});

export type CustomFont = z.infer<typeof customFontSchema>;

const fontsSchema = z
  .strictObject({
    detected: z.array(text(80)).max(8).optional(),
    // The brand font rendered in videos (EchoBrandOutro) — curated-only.
    selected: z.enum(CURATED_FONTS).optional(),
    custom: z.array(customFontSchema).max(MAX_CUSTOM_FONTS).optional(),
    title_font_id: z.string().min(1).max(80).optional(),
  })
  .superRefine((fonts, ctx) => {
    const ids = (fonts.custom ?? []).map((font) => font.id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: "custom",
        message: "duplicate font ids",
        path: ["custom"],
      });
    }
    if (
      fonts.title_font_id !== undefined &&
      !ids.includes(fonts.title_font_id) &&
      !(CURATED_FONTS as readonly string[]).includes(fonts.title_font_id)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "title_font_id must name an uploaded font or a curated family",
        path: ["title_font_id"],
      });
    }
  });

// prefs: known keys are validated; unknown keys round-trip untouched (the
// reserved bag both editors preserve). Arrays are still rejected.
const prefsSchema = z
  .object({
    narrator: z.enum(CURATED_NARRATORS).optional(),
    music_palette: z.enum(CURATED_MUSIC_PALETTES).optional(),
    powered_by: z.enum(POWERED_BY_VALUES).optional(),
  })
  .catchall(z.unknown());

export const brandProfileSchema = z.strictObject({
  // bool is not a literal 1 in JS strict equality, so true/false are rejected.
  schema_version: z.literal(1),
  identity: identitySchema.optional(),
  palette: paletteSchema.optional(),
  logo: logoSchema.nullish(),
  // Poster/thumbnail variant of the mark. One asset cannot serve both
  // surfaces: the poster is a generated image the mark is stamped onto (a
  // plated lockup reads best on photography), while the video outro
  // composites the mark onto a pale ripple plate, where that plate reads as
  // a white box. Absent -> both surfaces use `logo`.
  poster_logo: logoSchema.nullish(),
  contact: contactSchema.optional(),
  tone: toneSchema.optional(),
  fonts: fontsSchema.optional(),
  // narrator/music/powered_by prefs (validated) + the open remainder of the reserved bag.
  prefs: prefsSchema.optional(),
});

export type BrandProfile = z.infer<typeof brandProfileSchema>;

const isPlainEmptyObject = (value: unknown): boolean =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.keys(value as Record<string, unknown>).length === 0;

/**
 * Validate a brand document. Returns the profile, or `null` for "no profile
 * yet" (`null`/`undefined`/`{}`). Throws a ZodError on contract violations.
 */
export const parseBrandProfile = (value: unknown): BrandProfile | null => {
  if (value === null || value === undefined || isPlainEmptyObject(value)) {
    return null;
  }
  return brandProfileSchema.parse(value);
};

/** PT-050: the effective credit setting — an absent pref means "on". */
export const effectivePoweredBy = (
  profile: Pick<BrandProfile, "prefs"> | null | undefined,
): PoweredBy => (profile?.prefs?.powered_by === "off" ? "off" : "on");

/** Non-throwing variant for rendering unvalidated rows. */
export const safeParseBrandProfile = (
  value: unknown,
):
  | { ok: true; profile: BrandProfile | null }
  | { ok: false; error: string } => {
  try {
    return { ok: true, profile: parseBrandProfile(value) };
  } catch (error) {
    return { ok: false, error: String((error as Error).message ?? error) };
  }
};
